import * as Types from '../types';
import { Environment } from '../../../config';
import request from '../../../utils/request';
import { LanguagesEnum } from 'constants/languages';
import _ from 'lodash';
import { varToStringParams } from 'utils/path';
import EntityType from 'constants/entity';
import SharingType from 'constants/sharing';
import { IClientListItem } from '../client';
import { IDocument, IDocumentPayload } from 'constants/document';
import { INote } from 'constants/notes';
import { ITextMap } from 'components/atoms/TranslatedText/TranslatedTextV1';
import { ITask } from '../task';
import { IFiltersSelection } from 'components/molecules/ProductFilter/store/reducer';
import IBounds from 'constants/bounds';
import { UserRoles } from 'constants/authRoles';

/** fetchProductList  */

export type FiltersOutput = IFiltersSelection & { bounds: IBounds };

export interface IProductListOutput {
  items: IDataItems[];
  filters: FiltersOutput;
  paging: IPaging;
}

export interface ICollectionOutput {
  items: number;
  id: number;
  name: string;
  guid: string;
}

export interface IDataItems {
  id: number;
  ownerid: number | null;
  producttypeid: number | null;
  status: number | null;
  statusname: string;
  availabilitystatus: number | null;
  availability: string | null;
  published: boolean | null;
  visibility: string | null;
  name: string | null;
  names: any;
  price: number | null;
  pricetext: string | null;
  pricetextshort: string | null;
  defaultpricetext: string | null;
  fromprice: number | null;
  hideprice: number | null;
  pricetype: number | null;
  displayorder: number | null;
  deleted: boolean;
  createdonutc: Date;
  updatedonutc: Date;
  createdby: number | null;
  modifiedby: number | null;
  agentid: number | null;
  agent: string | null;
  creator: string | null;
  position: any;
  location: string | null;
  picture: string | null;
  pictures?: number[];
  bedrooms: IBasicInfoSpec | null;
  bathrooms: IBasicInfoSpec | null;
  total_covered_area: IBasicInfoSpec | null;
  total_floors: IBasicInfoSpec | null;
  propertySize: number | null;
  favourite: boolean;
  polygons?: any;
}

interface IPaging {
  page: number;
  pageSize: number;
  total: number;
}

export interface IProductListInput {
  guid?: string;
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  bounds?: IBounds;
  published?: number;
}

export interface IListProductsResponseOutput extends Types.ResponseOutput {
  data: IProductListOutput;
}

export const fetchProductList = async (
  payload: IProductListInput
): Promise<IListProductsResponseOutput> => {
  console.log('published', payload.published);

  const { guid, bounds, published, ...rest } = payload;
  const query = varToStringParams({
    variablesArray: [
      ...Object.keys({ ...rest, ...bounds }).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get({ ...rest, ...bounds }, key, undefined),
        })
      ),
      ...(guid
        ? [
            {
              key: 'sharingGuid',
              value: guid,
            },
          ]
        : []),
      ...[
        {
          key: 'published',
          value: published,
        },
      ],
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/list${query}`
  );

  let _data: IProductListOutput = data.data;
  let output: IListProductsResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

/** Product Detail */

/** Add Product */
export interface IAddProductInput {
  name: string | null;
  location: {
    lat: number;
    lng: number;
  };
}

interface IProductAddOutput extends Types.ResponseOutput {
  data: { id: number };
}

export const addProduct = async (
  payload: IAddProductInput
): Promise<IProductAddOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product `,
    {
      body: payload,
    }
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface ISegments {
  condition: ISpec;
  parking_type: ISpec;
  furnishing: ISpec;
  air_conditioning: ISpec;
  heating: ISpec;
  heating_type_: ISpec;
  shape: ISpec;
  zone: ISpec;
  energy_certificate: ISpec;
  languages: ISpec;
  property_status: ISpec;
  availability_status: ISpec;
  tax: ISpec;
  categories: ISpec;
  tags: ISpec;
  suitable_for: ISpec;
}

export interface ISpec {
  id: number;
  name: string;
  options: Array<ISpecOption> | null;
}

export interface ISpecOption {
  id: number;
  name: string;
  code?: string;
  flag?: string;
  parentcategoryid?: number;
  systemname?: string;
}

export const fetchBasicInfoSegments = async (language: LanguagesEnum) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/basic-details-segments/${language}`
  );
  let _data: ISegments = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface IMiniProfile {
  id: number;
  name: string;
  email: string;
}

export const fetchCustomers = async () => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/customers/mini-profile`
  );
  let _data: Array<IMiniProfile> = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchAgents = async () => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/agents/mini-profile`
  );
  let _data: Array<IMiniProfile> = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchCustomersByRole = async ({ role }: { role: UserRoles }) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/by-role?role=${role}`
  );
  let _data: Array<IMiniProfile> = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

interface IAttribute {
  attributetypeid: number;
  customvalue: string;
  productid: number;
  specificationattributeid: number;
  systemname?: string;
}
export interface ProductBasicInfo {
  id: number;
  picture: string | null;
  ownerid?: number;
  ownername?: string;
  status: number;
  availabilitystatus: number;
  attributes: IAttribute[];
  published: boolean;
  name: {
    default: LanguagesEnum;
    translations: Array<{
      language: LanguagesEnum;
      value: string;
    }>;
  };
  agentid?: number | null;
  agentname?: string;
  taxcategoryid?: number;
  price?: number;
  pricetype?: number;
  fromprice?: number;
  hideprice?: boolean;
  defaultpricetext?: string;
  property_subtype?: Array<{
    id: number;
    value: string;
  }>;
  description: {
    default: LanguagesEnum;
    translations: Array<{
      language: LanguagesEnum;
      value: string;
    }>;
  };
  bedrooms?: Array<IBasicInfoSpec>;
  bathrooms?: Array<IBasicInfoSpec>;
  condition?: Array<IBasicInfoSpec>;
  wc?: Array<IBasicInfoSpec>;
  internal_covered_areas?: Array<IBasicInfoSpec>;
  covered_verandas?: Array<IBasicInfoSpec>;
  uncovered_verandas?: Array<IBasicInfoSpec>;
  uncovered_parking?: Array<IBasicInfoSpec>;
  parking_spots?: Array<IBasicInfoSpec>;
  has_pool?: Array<IBasicInfoSpec>;
  basement?: Array<IBasicInfoSpec>;
  basement_area?: Array<IBasicInfoSpec>;
  attic?: Array<IBasicInfoSpec>;
  attic_area?: Array<IBasicInfoSpec>;
  total_covered_area?: Array<IBasicInfoSpec>;
  furnishing?: Array<IBasicInfoSpec>;
  air_conditioning?: Array<IBasicInfoSpec>;
  heating?: Array<IBasicInfoSpec>;
  heating_type_?: Array<IBasicInfoSpec>;
  year_built?: Array<IBasicInfoSpec>;
  floor?: Array<IBasicInfoSpec>;
  total_floors?: Array<IBasicInfoSpec>;
  covered_parking?: Array<IBasicInfoSpec>;
  plot_land_size?: Array<IBasicInfoSpec>;
  frontage?: Array<IBasicInfoSpec>;
  shape?: Array<IBasicInfoSpec>;
  water_supply?: Array<IBasicInfoSpec>;
  road_access?: Array<IBasicInfoSpec>;
  electricity?: Array<IBasicInfoSpec>;
  zone?: Array<IBasicInfoSpec>;
  building_density?: Array<IBasicInfoSpec>;
  max_coverage?: Array<IBasicInfoSpec>;
  max_floors?: Array<IBasicInfoSpec>;
  max_height?: Array<IBasicInfoSpec>;
  energy_certificate?: Array<IBasicInfoSpec>;
  property_videos: Array<IBasicInfoSpec>;
  property_virtual_tour: Array<IBasicInfoSpec>;
  part_of_project: Array<IBasicInfoSpec>;
  has_keys: Array<IBasicInfoSpec>;
  key_no?: Array<IBasicInfoSpec>;
  key_location?: Array<IBasicInfoSpec>;
  title_deed: Array<IBasicInfoSpec>;
  title_deed_no?: Array<IBasicInfoSpec>;
  mortgage: Array<IBasicInfoSpec>;
  bank_name?: Array<IBasicInfoSpec>;
  total_amount?: Array<IBasicInfoSpec>;
  remaining_amount?: Array<IBasicInfoSpec>;
  mortgage_start_date_?: Array<IBasicInfoSpec>;
  mortgage_end_date?: Array<IBasicInfoSpec>;
  invesment_and_roi?: Array<IBasicInfoSpec>;
  roi?: Array<IBasicInfoSpec>;
  rental_income_per_month?: Array<IBasicInfoSpec>;
  price_sold?: Array<IBasicInfoSpec>;
  date_sold?: Array<IBasicInfoSpec>;
  price_rented?: Array<IBasicInfoSpec>;
  date_signed?: Array<IBasicInfoSpec>;
  rental_start_date?: Array<IBasicInfoSpec>;
  rental_end_date?: Array<IBasicInfoSpec>;
  rented_to?: Array<IBasicInfoSpec>;
  refered_by?: Array<IBasicInfoSpec>;
  representative?: Array<IBasicInfoSpec>;
  suitable_for?: Array<IBasicInfoSpec>;
  note: {
    id: number;
    createdBy: string;
    createdOn: Date;
    note: string;
    pictures: Array<IDocument>;
  } | null;
  tags: Array<{ id: number; name: string }>;
}

export interface IBasicInfoSpec {
  id: number;
  custom_value: string;
  custom_value_type: string;
  name?: string;
  names?: { language: LanguagesEnum; value: string }[];
  icon?: string;
  iconcolor?: string;
  prefix?: string;
  systemname?: string;
  customvalue?: string;
}
export const fetchProductBasicInfo = async (productId: number) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${productId}/basic-details`
  );
  let _data: ProductBasicInfo = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface IUpdateProductBody {
  specs?: Array<{
    type: 'custom' | 'option';
    systemname: string;
    specificationId?: number;
    value?: string | number | boolean;
  }>;
  categories?: Array<{
    categoryId: number;
  }>;
  price?: number;
  taxId?: number;
  ownerId?: number;
  status?: number;
  availabilityStatus?: number;
  published?: boolean;
  description: Array<{
    language: LanguagesEnum;
    value: string;
  }>;
  name: Array<{
    language: LanguagesEnum;
    value: string;
  }>;
  agentId?: number;
  tags?: Array<{ id: number; name: string }>;
  price_sold?: string | null;
  date_sold?: string | null;
  price_rented?: string | null;
  date_signed?: string | null;
  rental_start_date?: string | null;
  rental_end_date?: string | null;
  rented_to?: string | null;
}

export const updateProductBasicInfo = async (
  body: IUpdateProductBody,
  productId: number
) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/product/${productId}/basic-details`,
    {
      body,
    }
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface IUpdateProductAmentities {
  specs?: Array<{
    type: 'custom' | 'option';
    systemname: string;
    specificationId?: number;
    value?: string | number | boolean;
  }>;
}
export const updateProductAmentities = async (
  body: IUpdateProductAmentities,
  productId: number
) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/product/${productId}/amentities`,
    {
      body,
    }
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const fetchProductLocation = async (productId: number) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${productId}/location`
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const updateProductLocation = async (productId: number, body: any) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/product/${productId}/location`,
    {
      body,
    }
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface IInsertProductNoteBody {
  pictures?: Array<IDocumentPayload>;
  note: string;
}

export const insertProductNote = async (
  body: IInsertProductNoteBody,
  productId: string
) => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/${productId}/note`,
    { body }
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const fetchProductNotes = async (productId: string) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${productId}/note`
  );
  let _data: Array<INote> = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const insertProductImages = async (
  productId: number,
  documents: Array<IDocumentPayload>
) => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/${productId}/documents?deletePrevious=false`,
    {
      body: {
        documents,
      },
    }
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const getProductImages = async (payload: {
  productId: number;
  name?: string;
}) => {
  let query = varToStringParams({
    variablesArray: [{ key: 'name', value: payload?.name }],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${payload.productId}/images${query}`
  );
  const _data = data.data as Array<IDocument>;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface IUpdateDocumentArrangementsPayload {
  pictureId: number;
  displayOrder: number;
}
export const rearrangeProductImages = async (
  productId: number,
  documents: Array<IUpdateDocumentArrangementsPayload>
) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/product/${productId}/documents-rearrange`,
    {
      body: {
        documents,
      },
    }
  );
  return data;
};

export const getProductFloorplans = async (productId: number) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${productId}/floorplans`
  );
  const _data = data.data as Array<IDocument>;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
export const fetchProductName = async () => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/list/name`
  );
  const _data = data.data as Array<IDocument>;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const getProductDocuments = async (productId: number) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${productId}/documents`
  );
  const _data = data.data as Array<IDocument>;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const deleteNote = async (noteId: number) => {
  await request('delete', `${Environment.API_BASE_URL}/api/note/${noteId}`);
};

export interface IProductSharingPayload {
  sharingType: SharingType;
  ids?: Array<number>;
  type?: Array<number>;
  subtype?: Array<number>;
  bedrooms?: number;
  bathrooms?: number;
  minYearBuilt?: number;
  minPropertySize?: number;
  minPrice?: number;
  maxPrice?: number;
  agents?: Array<number>;
  location?: {
    city: Array<number>;
    municipality: Array<number>;
  };
  dateAdded?: string;
  other?: {
    [key: string]: string | undefined;
  };
  bounds?: IBounds;
  [key: string]: any;
}

interface IFileData {}

interface IProductSharingOutput extends Types.ResponseOutput {
  data: {
    id: number;
    guid: string;
  };
}
export const insertProductSharing = async (
  payload: IProductSharingPayload
): Promise<IProductSharingOutput> => {
  const { data }: any = await request(
    'post',
    `${
      Environment.API_BASE_URL
    }/api/sharing/${EntityType.Product.toLocaleLowerCase()}`,
    {
      body: payload,
    }
  );
  return data;
};

interface IHomeProductOutput extends Types.ResponseOutput {
  data: HomeProductData;
}

export type HomeProductData = Array<{
  id: number;
  name: string;
  products: Array<{
    id: number;
    ownerid: number;
    producttypeid: number;
    status: number;
    statusname: string;
    availabilitystatus: number;
    availability: string;
    published: boolean;
    name: string;
    price: number;
    displayorder: number;
    deleted: boolean;
    createdonutc: string;
    updatedonutc: string;
    createdby: number;
    modifiedby: number;
    agentid: number | null;
    agent: string;
    creator: string;
    picture: string;
    bedrooms: number;
    bathrooms: number;
    propertySize: number;
  }>;
}>;

export const homePageProducts = async (): Promise<IHomeProductOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/homepage`
  );
  return data;
};

interface IProductAssignedToClientsOutput extends Types.ResponseOutput {
  data: IProductAssignedToClients;
}

interface IProductMatchedClientsOutput extends Types.ResponseOutput {
  data: IProductMatchedClients;
}

export interface IProductAssignedToClients {
  items: Array<IClientListItem>;
  paging: IPaging;
}

export interface IProductAssignedToClientsInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  id: number;
}

export interface IProductMatchedClientsInput {
  sortField?: string;
  page?: number;
  pageSize?: number;
  order?: 'asc' | 'desc';
  id: number;
}

export interface IMatchedClientListItem {
  guid: string;
  products: number[];
  clientemail: string;
  clientname: string;
  clientavatar: string;
  isMatched: boolean;
  createdonutc: string;
  id?: number;
}

export interface IProductMatchedClients {
  items: Array<IMatchedClientListItem>;
  paging?: IPaging;
}

export const fetchProductAssignedToClients = async (
  payload: IProductAssignedToClientsInput
): Promise<IProductAssignedToClientsOutput> => {
  const { id, ...rest } = payload;
  const query = varToStringParams({
    variablesArray: [
      ...Object.keys(rest).map((key: string, index: number) => ({
        key: key,
        value: _.get(rest, key, undefined),
      })),
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${payload.id}/assigned-to-clients${query}`
  );
  const _data = data.data as IProductAssignedToClientsOutput['data'];
  let output: IProductAssignedToClientsOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchProductMatchedClients = async (
  payload: IProductMatchedClientsInput
): Promise<IProductMatchedClientsOutput> => {
  const { id, ...rest } = payload;
  const query = varToStringParams({
    variablesArray: [
      ...Object.keys(rest).map((key: string, index: number) => ({
        key: key,
        value: _.get(rest, key, undefined),
      })),
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${payload.id}/matched-clients${query}`
  );
  const _data = data.data as IProductMatchedClientsOutput['data'];
  let output: IProductMatchedClientsOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

/** Product Details */
interface IPropertyData {
  id: number | null | undefined;
  title: string;
  custom_value?: string;
  custom_value_type?: string;
  value?: string;
  storeid?: number;
  icon?: string;
  iconcolor?: string | undefined;
  picture?: string;
  prefix?: string;
  type?: number;
}

interface IFileData {
  id: number;
  displayorder: number;
  mimetype: string;
  guid: string;
  caption: string;
  filename: string;
  visible: boolean;
  uploadedon: string;
  uploadedby: string;
  virtualpath: string;
  fileType: number;
  fileContentType: number;
  payload: null | Partial<{
    floor: string;
    size: number;
    bedrooms: string;
    bathrooms: string;
    additionalInfo: string;
  }>;
}

interface IUserData {
  id: number;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
}
export interface IProductDetails {
  id: number;
  ownerid: number | null;
  producttypeid: number | null;
  status: number | null;
  statusname: string | null;
  availabilitystatus: number | null;
  availability: string | null;
  published: boolean | null;
  name: string | null;
  names: any;
  price: number | null;
  defaultpricetext: string | null;
  pricetext: string | null;
  vat_option: string | null;
  fromprice: number | null;
  hideprice: number | null;
  pricetype: number | null;
  displayorder: number | null;
  deleted: boolean | null;
  createdonutc: string | null;
  updatedonutc: string | null;
  modifiedby: number | null;
  agentid: number | null;
  createdby: number | null;
  addressid: number | null;
  highlights: Partial<{
    property_videos: Array<IPropertyData>;
    property_virtual_tour: Array<IPropertyData>;
    bedrooms: Array<IPropertyData>;
    wc: Array<IPropertyData>;
    internal_covered_areas: Array<IPropertyData>;
    covered_verandas: Array<IPropertyData>;
    uncovered_verandas: Array<IPropertyData>;
    bathrooms: Array<IPropertyData>;
    has_pool: Array<IPropertyData>;
    basement: Array<IPropertyData>;
    attic: Array<IPropertyData>;
    attic_size: Array<IPropertyData>;
    total_covered_area: Array<IPropertyData>;
    year_built: Array<IPropertyData>;
    total_floors: Array<IPropertyData>;
    floor: Array<IPropertyData>;
    parking_spots: Array<IPropertyData>;
    plot_land_size: Array<IPropertyData>;
    frontage: Array<IPropertyData>;
    water_supply: Array<IPropertyData>;
    road_access: Array<IPropertyData>;
    electricity: Array<IPropertyData>;
    building_density: Array<IPropertyData>;
    part_of_project: Array<IPropertyData>;
    has_keys: Array<IPropertyData>;
    title_deed: Array<IPropertyData>;
    mortgage: Array<IPropertyData>;
    condition: Array<IPropertyData>;
    parking_type: Array<IPropertyData>;
    furnishing: Array<IPropertyData>;
    shape: Array<IPropertyData>;
    zone: Array<IPropertyData>;
    energy_certificate: Array<IPropertyData>;
  }> | null;
  description: ITextMap | null;
  tags: Array<{
    id: number;
    name: string;
  }> | null;
  pictures: Array<IFileData> | null;
  floorplans: Array<IFileData> | null;
  documents: Array<IFileData> | null;
  owner: IUserData | null;
  agent: IUserData | null;
  createdBy: IUserData | null;
  address: {
    id: number;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    position: {
      srid: number | null;
      version: number;
      points: Array<{
        x: number;
        y: number;
        z: number | null;
        m: number | null;
      }>;
      figures: Array<{
        attribute: number;
        pointOffset: number;
      }> | null;
      shapes: Array<{
        parentOffset: number;
        figureOffset: number;
        type: number;
      }> | null;
      segments: Array<any> | null;
    };
    polygons?: any;
    company: null | number;
    countryid: number;
    stateprovinceid: null | number;
    county: string | null;
    city: string | null;
    area: string | null;
    municipality: string | null;
    address1: string | null;
    address2: string | null;
    zippostalcode: string | null;
    phonenumber: string | null;
    faxnumber: string | null;
    customattributes: null;
    createdonutc: string | null;
    radius: number | null;
    displaysetting: number | null;
    sheet: number | null;
    plan: null | null;
    parcelnumber: string | null;
    block: number | null;
  } | null;
  priceHistory: Array<{
    id: number;
    customerId: number;
    value?: string;
    createdOnUtc?: string;
    creator?: string;
  }> | null;
  visitHistory: {
    [key: string]: Array<{
      id: number;
      customerId: number;
      value?: string;
      createdOnUtc?: string;
      creator?: string;
    }>;
  };
  favourite: boolean;
}

export const fetchProductDetails = async (
  id: string,
  language: LanguagesEnum
): Promise<Types.ResponseOutputV2<IProductDetails>> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${id}?language=${language}`
  );
  return data;
};

export const fetchProductActivityFeed = async (
  productId: number
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/activitylog/by-product/${productId}`
  );
  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchProductDocument = async (
  id: string | number
): Promise<Types.ResponseOutputV2<IProductDetails>> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${id}/additional_documents`
  );
  return data;
};

/** Fetch Similar Products */

export interface ISimilarProducts {
  id: number;
  name: string;
  products: IDataItems[];
}

export const fetchSimilarProducts = async (
  id: string | number
): Promise<Types.ResponseOutputV2<ISimilarProducts[]>> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${id}/similar_products`
  );
  return data;
};

/** Add Bookviewing */

export const addBookViewing = async (
  payload: ITask
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/${payload?.products[0]}/bookviewing`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

/** Add RequestInfo */

export const addRequestInfo = async (
  payload: ITask
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/${payload?.products[0]}/requestinfo`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const addToFavourite = async (payload: { id: number }): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/${payload.id}/favourites`
  );

  let _data: any = data.data;
  let output: IListProductsResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
export const removeFromFavourite = async (payload: {
  id: number | string;
}): Promise<any> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/product/${payload.id}/favourites`
  );

  let _data: any = data.data;
  let output: IListProductsResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
// get paralist
export type DataItemParalist = {
  id: number;
  name: string;
};
export interface IParalistOutput extends Types.ResponseOutput {
  data: DataItemParalist[];
}

export const getParalistPointOfInterest =
  async (): Promise<IParalistOutput> => {
    const { data }: any = await request(
      'get',
      `${Environment.API_BASE_URL}/api/paralist/getByGroupcode?groupcode=pointofinterest.type`
    );

    let _data: DataItemParalist[] = data.data;
    let output: Types.ResponseOutput = {
      message: data.message,
      data: _data,
      status: data.status,
    };
    return output;
  };

export interface IBookviewing {
  id: number;
  title: string;
  description: string;
  type: number;
  datestart: string;
  dateend: string;
  keepprivate: boolean;
  tags: any;
  color: any;
  addressid: number | null;
  createdon: string;
  status: number;
  extra: string;
  assignee: {
    id: number;
    name: string;
  };
  customer?: string | null;
  avatar?: string | null;
}

export const fetchExistingBookviewing = async (
  productid: number
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${productid}/bookviewing`
  );

  let _data: IBookviewing = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchExistingRequestInfo = async (
  productid: number
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${productid}/requestinfo`
  );

  let _data: IBookviewing = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

/** Product chatgroup details */
export interface ICreateAndFetchProductChat {
  id: number;
  guid: string;
  title: string;
  createdby: number | null;
  status: number | null;
  type: number | null;
  private: null | boolean;
  createdonutc: string | null;
  lastupdate: string | null;
  storeid: number | null;
}

export const createAndFetchProductChat = async (
  productId: number,
  sessionGuid: string
): Promise<Types.ResponseOutputV2<ICreateAndFetchProductChat>> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/chat`,
    {
      body: {
        productId,
        sessionGuid,
      },
    }
  );

  return data;
};
export interface IAssignToClientsInput {
  clientIds: number[];
  productIds: number[];
  name?: string;
  guid?: string;
  email?: string;
  documents?: IDocumentSendMail[];
  note?: string;
}

export interface IListAssignClientsOutput extends Types.ResponseOutput {
  data: {
    collection: {
      createdby: number;
      createdonutc: string;
      customerid: number;
      guid: string;
      id: number;
      name: string;
      storeid: number;
      type: number;
      picture: string;
    };
    items: {
      entityid: number;
      entityname: string;
      id: number;
      params: string | null;
      sharingid: number;
    }[];
  }[];
}

export const addAssignToClients = async (
  payload: IAssignToClientsInput
): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/assign_to_clients`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: IListAssignClientsOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface IGetAssignToClientsInput {
  clientid: number;
}

export const getAssignToClients = async (
  payload: IGetAssignToClientsInput
): Promise<any> => {
  const query = varToStringParams({
    variablesArray: [
      ...Object.keys(payload).map((key: string, index: number) => ({
        key: key,
        value: _.get(payload, key, undefined),
      })),
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/assign/clients${query}`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: IListProductsResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface IAddToCollectionsInput {
  collectionIds: number[];
  productIds: number[];
  clientIds?: number[] | null;
}

export const addToCollections = async (
  payload: IAddToCollectionsInput
): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/add_to_collections`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: IListProductsResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const shareCollection = async (payload: {
  clientIds: number[];
  collectionId: number;
}): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/share-collection`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: IListProductsResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

// get list property viewed

export interface IViewedListOutput {
  EntityId: number;
}
export interface IListViewedsResponseOutput extends Types.ResponseOutput {
  data: IViewedListOutput[];
}

export const getListViewed = async (): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/viewed`
  );

  let _data: any = data.data;
  let output: IListViewedsResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface ISendMailInput {
  sharingType: number;
  ids: number[];
  email: string;
}

export const sendEmail = async (payload: ISendMailInput): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/send-mail`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: any = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

interface IDocumentSendMail {
  path?: string;
  displayName?: string;
}

export interface ISendMailAdvanceInput {
  sharingType: number;
  ids: number[];
  emails?: string[];
  documents: IDocumentSendMail[];
  comment?: string;
}

export const sendEmailAdvance = async (
  payload: ISendMailAdvanceInput
): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/send-mail-advance`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: any = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface ISendCloneProductInput {
  products: number[];
  emails: string[];
}

export const sendCloneProduct = async (
  payload: ISendCloneProductInput
): Promise<any> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/product/send-clone`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: any = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface IProductTagListInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  name: string;
}

export interface IDataItemsTags {
  id: number;
  name: string;
}

export interface IProductTagListOutput {
  items: IDataItemsTags[];
  paging: IPaging;
}

export interface IProductTagListInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  name: string;
}

export const fetchProductTagList = async (
  payload: IProductTagListInput
): Promise<Types.ResponseOutput> => {
  let query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
      { key: 'name', value: payload?.name },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/producttag/list${query}`
  );

  let _data: IProductTagListOutput = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

interface IProductTagEditOutput extends Types.ResponseOutput {
  data: {};
}

export const deleteProductTag = async ({
  id,
}: {
  id: number;
}): Promise<IProductTagEditOutput> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/producttag/${id}`
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface IProductTagEditInput {
  name: string | null;
}

export const editProductTag = async (payload: {
  id: number;
  name: string;
}): Promise<IProductTagEditOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/producttag`,
    { body: payload }
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface IGetProductPromotionsOutput extends Types.ResponseOutput {
  data: {
    id: number;
    promotionType: number;
    promotionDate: string;
    showOnMainStore?: boolean;
  };
}
export interface IGetProductPromotionsInput {
  id: string | number;
}

export const fetchProductPromotions = async ({
  id,
}: IGetProductPromotionsInput): Promise<IGetProductPromotionsOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/product/${id}/promotions`
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

interface IUpdateProductPromotionsOutput extends Types.ResponseOutput {
  data: {};
}
export interface IUpdateProductPromotionsInput {
  id: string | number;
  promotionType: number;
  promotionDate: string | null;
  showOnMainStore: boolean;
}

export const updateProductPromotions = async (
  payload: IUpdateProductPromotionsInput
): Promise<IUpdateProductPromotionsOutput> => {
  const { id, ...rest } = payload;
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/product/${id}/promotions`,
    { body: rest }
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const deletePropertyFromCollection = async (payload: {
  propertyIds: number[];
  guid: string;
}): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/product/remove-from-collection`,
    { body: payload }
  );

  let _data = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
